import { css, SerializedStyles } from '@emotion/react';
import type { ComponentType } from 'react';

import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { CompleteIcon, InProgressIcon, NotStartedIcon, ReworkIcon } from '@kaiizen/kai-shiki/react';

/**
 * Presentation kept separate from TaskStatusBadge's own logic, the same way CountInput's
 * `styles` prop does (see `CountInput/countInputStyles.ts`) — a re-skin would spread
 * `DEFAULT_TASK_STATUS_BADGE_STYLES` and override only what differs, the way
 * `LABELED_SELECT_STYLES` re-skins `DEFAULT_LABELED_SELECT_STYLES`. Only the one default
 * exists today — no app surface has ever needed a second size, so there's no `size` prop
 * to carry it; a future larger badge is exactly that kind of re-skin.
 */

/** One entry per {@link OverallTaskStatus} — its color pair, and (for the four states the
 *  work ladder actually walks through) the kai-shiki icon that leads it. A state with no
 *  `Icon` shows the default marker (`styles.defaultSizes`, a plain currentColor dot) only
 *  when `default` is set — otherwise it leads with nothing at all. Labels are content, not
 *  style — see `TASK_STATUS_BADGE_LABEL` in `TaskStatusBadge.tsx`. */
export type TaskStatusBadgeStatusStyle = {
  tone: { color: string; background: string; border?: string };
  Icon?: ComponentType<{ size?: number; color?: string }>;
  default?: boolean;
};

export type TaskStatusBadgeStyles = {
  base: SerializedStyles;
  iconSize: number;
  /** The marker for statuses with no `Icon` of their own but `default: true` */
  defaultMarker: SerializedStyles;
  /** `iconOnly` mode's container — one fixed size, no chip background, since the
   *  label moves to `title`/`aria-label` instead. Still rounded so a status whose
   *  `tone.border` is set (e.g. `empty`'s dashed border) reads as the same shape
   *  as the chip, not a square around it. */
  iconOnlyContainer: SerializedStyles;
  iconOnlyIconSize: number;
  iconOnlyDefaultMarker: SerializedStyles;
  statusStyles: Record<OverallTaskStatus, TaskStatusBadgeStatusStyle>;
};

const toneStyle = (color: string, background: string, border?: string) => ({ color, background, border });

export const DEFAULT_TASK_STATUS_BADGE_STYLES: TaskStatusBadgeStyles = {
  /** The styleguide `.pill`, verbatim — mono/bold, wide tracking, dense-row sizing
   *  (the only sizing any surface has ever used). */
  base: css`
    label: TASK_STATUS_BADGE;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.07em;
    text-transform: uppercase;
    white-space: nowrap;
    padding: 2px 7px 1px;
    border-radius: 4px;
  `,
  iconSize: 10,
  defaultMarker: css`
    flex: none;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  `,
  iconOnlyContainer: css`
    label: TASK_STATUS_BADGE_ICON_ONLY;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex: none;
    border-radius: 4px;
  `,
  iconOnlyIconSize: 14,
  iconOnlyDefaultMarker: css`
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  `,
  statusStyles: {
    empty: { tone: toneStyle(COLORS.midtone, 'transparent', `1px dashed ${COLORS.stroke}`), Icon: NotStartedIcon },
    draft: { tone: toneStyle(COLORS.midtone, COLORS.sunken), Icon: InProgressIcon },
    approved: { tone: toneStyle(COLORS.success, COLORS.successFill), Icon: CompleteIcon },
    notStarted: { tone: toneStyle(COLORS.midtone, COLORS.sunken), Icon: NotStartedIcon },
    inProgress: { tone: toneStyle(COLORS.info, COLORS.infoFill), Icon: InProgressIcon },
    needsRework: { tone: toneStyle(COLORS.error, COLORS.errorFill), Icon: ReworkIcon },
    completed: { tone: toneStyle(COLORS.success, COLORS.successFill), Icon: CompleteIcon },
  },
};
